/**
 * useCosignContext Hook
 *
 * Derives co-sign workflow context from the current user and note data.
 * Determines whether the current user is the author or a supervisor,
 * whether the note requires co-signing, and the current co-sign status.
 */

import { useMemo } from 'react';
import { useUser, useUserRoles } from '@features/user';
import type { NoteResponse } from '@store/generated/notes';
import { hasBillingAccess, hasRole } from '@utils/roleUtils';

export type CosignStatus = 'none' | 'ready' | 'edits_requested' | 'signed';

export interface CosignContext {
  /** Whether the current user authored this note */
  isAuthor: boolean;
  /** Whether the current user has the supervisor role */
  isSupervisor: boolean;
  /** Whether the current user has a billing admin role */
  isBillingAdmin: boolean;
  /** Whether co-sign is enabled and this note requires it */
  needsCosign: boolean;
  /** Current co-sign status derived from completion_status */
  cosignStatus: CosignStatus;
}

const COSIGN_STATUSES = new Set<string>(['ready', 'edits_requested', 'signed']);

export function useCosignContext(noteData: NoteResponse | undefined): CosignContext {
  const { userState } = useUser();
  const userRoles = useUserRoles();

  return useMemo<CosignContext>(() => {
    const currentUserId = userState?.user_id;
    const noteUserId = noteData?.issuing_user?.user_id;

    const isAuthor = !!(currentUserId && noteUserId && currentUserId === noteUserId);
    const isSupervisor = hasRole(userRoles, 'supervisor');
    const isBillingAdmin = hasBillingAccess(userRoles);
    const needsCosign = !!noteData?.requires_cosigning;

    const completionStatus = noteData?.completion_status ?? '';
    const cosignStatus: CosignStatus = COSIGN_STATUSES.has(completionStatus)
      ? (completionStatus as CosignStatus)
      : 'none';

    return { isAuthor, isSupervisor, isBillingAdmin, needsCosign, cosignStatus };
  }, [userState?.user_id, noteData, userRoles]);
}
